<script setup lang="ts">
    import EventIcon from './EventIcon.vue';
    import { computed, inject, ref } from 'vue';
    import FeatureLock from '@shared/components/FeatureLock.vue';
    import Modal from '@shared/components/Modal.vue';
    import BsAlert from '@shared/components/BsAlert.vue';
    import PrePaid from '@views/TextMessages/Shared/PrePaid.vue';
    import EventIssues from '@views/Event/EventIssues.vue';
    import EventPublishChecklist from '@views/Event/EventPublishChecklist.vue';
    import { sharedEventIssuesState } from '@views/Event/EventIssues';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ScopeAccess } from '@shared/directives/Scope.access';
    import { Permissions } from '@shared/Root/Permissions';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { setupEventValidation, toEventRefs, getEventSegmentId } from '@views/Event/Event';
    import { endEvent, pauseEvent, publishEvent } from '@views/Event/Event.access';
    import { type Event, type EventSegmentRelation, type ReadyForSchedulingItem, CommunicationItemChannel, EventStatusNames } from '@views/Event/EventTypes';

    const props = defineProps<{
        event: Event;
        canWrite: boolean;
    }>();

    const emit = defineEmits<{
        (ev: 'update:status', status: EventStatusNames): void;
        (ev: 'published'): void;
    }>();

    const updateEnabled = defineModel<boolean>('updateEnabled', { required: true });
    const canEdit = computed(() => props.canWrite && updateEnabled.value);

    // Which action is in flight, so only the clicked button shows the busy state while all are disabled
    const busyAction = ref<'publish' | EventStatusNames.Paused | EventStatusNames.Ended | null>(null);
    const isBusy = computed(() => busyAction.value !== null);

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const canExecute = computed(() => scopeAccess.hasScope({ Feature: 'Events', Permission: Permissions.Execute }));

    const eventIsPublishable = setupEventValidation({
        name: computed(() => props.event.Name ?? ''),
        participantStatusChangeIssueId: computed(() => props.event.ParticipantStatusChangeIssueId ?? ''),
        defaultSurveyId: computed(() => props.event.DefaultSurveyId ?? ''),
        requireAttestation: computed(() => props.event.RequireAttestation),
        attestationIssueId: computed(() => props.event.AttestationIssueId ?? ''),
    });

    const status = computed(() => props.event.Status);
    const statusLabel = computed(() => ($Resources as Record<string, string>)['EventStatus' + status.value] ?? String(status.value));

    // Relative-schedule events pick which communications to schedule in a modal before publishing (parity with PublishEvent.vue)
    const showScheduleModal = ref(false);
    const tempHideScheduleModal = ref(false);
    const readyForSchedulingItems = ref<ReadyForSchedulingItem[]>([]);
    const eventRefs = computed(() => toEventRefs(props.event));
    const getSegmentId = (relation: EventSegmentRelation) => getEventSegmentId(props.event, relation);

    const hasTextMessages = computed(() => readyForSchedulingItems.value.some((item) => item.Channel === CommunicationItemChannel.Sms));

    const handlePublish = () => {
        if (isBusy.value) return;

        if (props.event.UseRelativeSchedule) {
            showScheduleModal.value = true;
            return;
        }

        publish([]);
    };

    const handlePublishScheduled = async () => {
        if (isBusy.value) return;

        // EventIssues reloads its items when isPublishing goes back to false
        const sharedState = sharedEventIssuesState.get(props.event.EventId);
        sharedState && (sharedState.isPublishing = true);

        try {
            const published = await publish(readyForSchedulingItems.value);
            if (published) showScheduleModal.value = false;
        } finally {
            sharedState && (sharedState.isPublishing = false);
        }
    };

    const publish = async (itemsToSchedule: ReadyForSchedulingItem[]): Promise<boolean> => {
        busyAction.value = 'publish';
        try {
            await publishEvent(props.event.EventId, itemsToSchedule);
            emit('update:status', EventStatusNames.Active);
            updateEnabled.value = false;
            emit('published');
            bsAlert({ type: AlertTypes.Success, title: $Resources.EventHeading, content: $Resources.EventStatusActive });
            return true;
        } catch (error) {
            const xhr = error as { status?: number; statusText?: string; message?: string } | undefined;
            const errorMessage = (xhr?.statusText ?? '') + (xhr?.message ?? '');
            const isNoEmailQuestion = errorMessage.indexOf('No email question on survey') >= 0;
            const isOutsideCategoryWindow = errorMessage.indexOf('Sending outside the allowed window for this category') >= 0;

            if (xhr?.status === 400 && isNoEmailQuestion) {
                bsAlert({
                    type: AlertTypes.Danger,
                    title: $Resources.ErrorCouldNotPublishSurveyNoEmailOnSurveyHeading,
                    content: $Resources.ErrorCouldNotPublishSurveyNoEmailOnSurveyBody,
                });
            } else if (xhr?.status === 400 && isOutsideCategoryWindow) {
                bsAlert({ type: AlertTypes.Danger, content: errorMessage });
            } else {
                bsAlert({
                    type: AlertTypes.Danger,
                    title: $Resources.ErrorCouldNotPublishSurveyHeading,
                    content: $Resources.ErrorCouldNotPublishSurveyBody,
                });
            }
            return false;
        } finally {
            busyAction.value = null;
        }
    };

    const changeStatus = async (newStatus: EventStatusNames.Paused | EventStatusNames.Ended) => {
        busyAction.value = newStatus;
        try {
            await (newStatus === EventStatusNames.Paused ? pauseEvent(props.event.EventId) : endEvent(props.event.EventId));
            // Legacy unlocked before the call; doing it on success keeps a failed pause/end from unlocking an Active event.
            updateEnabled.value = true;
            emit('update:status', newStatus);
            bsAlert({
                type: AlertTypes.Success,
                title: $Resources.EventHeading,
                content: newStatus === EventStatusNames.Paused ? $Resources.EventStatusPaused : $Resources.EventStatusEnded,
            });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCouldNotEndSurveyHeading, content: $Resources.ErrorCouldNotEndSurveyBody });
        } finally {
            busyAction.value = null;
        }
    };

    const publishDisabled = computed(() => !props.canWrite || !eventIsPublishable.value || isBusy.value);
</script>

<template>
    <div class="m-sm-b">
        <p class="d-flex align-items-center justify-content-center">
            <EventIcon icon="circle" :size="16" class="status-circle m-xs-r" :class="'status-' + status.toLowerCase()" />
            <span>
                {{ $Resources.EventCurrentStatusLabel }}:
                <strong>{{ statusLabel.toLowerCase() }}</strong>
            </span>
        </p>

        <div v-if="canExecute" class="d-flex flex-direction-col gap-xs">
            <FeatureLock feature="Events.Publish">
                <button
                    v-if="status === EventStatusNames.Draft"
                    type="button"
                    class="btn btn-primary btn-block"
                    :class="{ 'is-busy': busyAction === 'publish' }"
                    :disabled="publishDisabled"
                    @click="handlePublish">
                    {{ $Resources.EventStatusPublishBtnText }}
                </button>

                <button
                    v-if="status === EventStatusNames.Active && event.UseRelativeSchedule"
                    type="button"
                    class="btn btn-primary btn-block"
                    :class="{ 'is-busy': busyAction === 'publish' }"
                    :disabled="publishDisabled || !canEdit"
                    @click="handlePublish">
                    {{ $Resources.EventStatusRepublishBtnText }}
                </button>

                <template #locked>
                    <button v-if="status === EventStatusNames.Draft" type="button" class="btn btn-primary btn-block" disabled>
                        {{ $Resources.EventStatusPublishBtnText }}
                    </button>

                    <button v-if="status === EventStatusNames.Active && event.UseRelativeSchedule" type="button" class="btn btn-primary btn-block" disabled>
                        {{ $Resources.EventStatusRepublishBtnText }}
                    </button>
                </template>
            </FeatureLock>

            <template v-if="status === EventStatusNames.Active">
                <button
                    type="button"
                    class="btn btn-default btn-block"
                    :class="{ 'is-busy': busyAction === EventStatusNames.Paused }"
                    :disabled="isBusy || !canEdit"
                    @click="changeStatus(EventStatusNames.Paused)">
                    {{ $Resources.EventStatusPauseBtnText }}
                </button>

                <button
                    type="button"
                    class="btn btn-subtle btn-block"
                    :class="{ 'is-busy': busyAction === EventStatusNames.Ended }"
                    :disabled="isBusy || !canEdit"
                    @click="changeStatus(EventStatusNames.Ended)">
                    {{ $Resources.EventStatusEndBtnText }}
                </button>
            </template>

            <FeatureLock v-if="status === EventStatusNames.Paused || status === EventStatusNames.Ended" feature="Events.Publish">
                <button type="button" class="btn btn-primary btn-block" :class="{ 'is-busy': busyAction === 'publish' }" :disabled="publishDisabled" @click="handlePublish">
                    {{ $Resources.EventStatusRepublishBtnText }}
                </button>

                <template #locked>
                    <button type="button" class="btn btn-primary btn-block" disabled>
                        {{ $Resources.EventStatusRepublishBtnText }}
                    </button>
                </template>
            </FeatureLock>

            <button
                v-if="status === EventStatusNames.Paused"
                type="button"
                class="btn btn-subtle btn-block"
                :class="{ 'is-busy': busyAction === EventStatusNames.Ended }"
                :disabled="isBusy || !canWrite"
                @click="changeStatus(EventStatusNames.Ended)">
                {{ $Resources.EventStatusEndBtnText }}
            </button>
        </div>

        <EventPublishChecklist
            v-if="!eventIsPublishable"
            class="m-sm-t"
            :name="event.Name ?? ''"
            :default-survey-id="event.DefaultSurveyId ?? ''"
            :participant-status-change-issue-id="event.ParticipantStatusChangeIssueId ?? ''"
            :require-attestation="event.RequireAttestation"
            :attestation-issue-id="event.AttestationIssueId ?? ''"
            :event-id="event.EventId" />
    </div>

    <Modal
        v-if="event.UseRelativeSchedule"
        :title="status === EventStatusNames.Draft ? $Resources.EventStatusPublishBtnText : $Resources.EventStatusRepublishBtnText"
        :show="showScheduleModal && !tempHideScheduleModal"
        size="xl"
        @close="showScheduleModal = false">
        <template #content>
            <p class="m-0-t m-lg-b">{{ $Resources.EventPublishInfoText }}</p>

            <EventIssues
                :event="eventRefs"
                :getSegmentId="getSegmentId"
                :isPublishEvent="true"
                :showPublishEvent="showScheduleModal"
                :updateEnabled="canEdit"
                @showPreview="tempHideScheduleModal = !tempHideScheduleModal"
                @update:readyForScheduling="(items) => (readyForSchedulingItems = items)" />

            <BsAlert
                :show="hasTextMessages"
                :type="AlertTypes.Info"
                :title="$Resources.EventPublishSmsBalanceInfoHeading"
                :content="$Resources.EventPublishSmsBalanceInfoText"
                :dismissible="false">
                <template #content>
                    <PrePaid class="m-xxs-t" />
                </template>
            </BsAlert>
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="isBusy || !canWrite" :class="{ 'is-busy': busyAction === 'publish' }" @click="handlePublishScheduled">
                {{ status === EventStatusNames.Draft ? $Resources.EventStatusPublishBtnText : $Resources.EventStatusRepublishBtnText }}
            </button>
        </template>
    </Modal>
</template>
